/**
 * Press Box → Stats. 2026-10-21 (per Gilles).
 *
 * The flagship stats page: NHL-style tables for every player, fuzzy search,
 * sortable columns, a drill-down from any player to the per-game lines and
 * the individual heard events (with the narrator's words — this is the
 * coach's view), and CSV / Excel / PDF export. The same StatsExplorer
 * renders the public page, so what the coach sees here is exactly what the
 * families see from the shared link (minus quotes and unpublished games).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import PressBoxShell from './PressBoxShell.jsx';
import StatsExplorer from '../../components/stats/StatsExplorer.jsx';

export default function PressBoxStatsPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [season, setSeason] = useState('');
  const [data, setData] = useState(null);
  const [timeFormat, setTimeFormat] = useState('12h');
  const [hiddenCols, setHiddenCols] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: ov }] = await Promise.all([
        api.get(`/teams/${teamId}/press-box/season`, { params: season ? { season } : {} }),
        api.get(`/teams/${teamId}/press-box`).catch(() => ({ data: null })),
      ]);
      setData(d);
      if (ov?.timeFormat) setTimeFormat(ov.timeFormat);
      setHiddenCols(ov?.statColumns?.private || []);
    } catch (e) {
      setData({ error: e.response?.data?.error || 'Could not load the stats' });
    }
  }, [teamId, season]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSeason(''); }, [teamId]);

  const fetchBreakdown = useCallback(async (row) => {
    const key = row.userId || (row.label === 'Unknown' ? 'unknown' : (row.number != null ? `num:${row.number}` : null));
    if (!key) return null;
    const { data: b } = await api.get(`/teams/${teamId}/press-box/player-breakdown`,
      { params: { player: key, ...(season ? { season } : {}) } });
    return b;
  }, [teamId, season]);

  return (
    <PressBoxShell teamId={teamId} userId={user?.id}>
      {!data && <div className="text-secondary text-sm p-4">Loading…</div>}
      {data?.error && <div className="card text-center p-4">{data.error}</div>}
      {data && !data.error && (
        <StatsExplorer
          teamName="Team stats"
          record={data.record}
          games={data.games || []}
          skaters={data.skaters || []}
          goalies={data.goalies || []}
          updatedAt={data.statsUpdatedAt}
          timeFormat={timeFormat}
          seasons={data.seasons}
          season={season || data.seasonId || 'all'}
          onSeason={setSeason}
          fetchBreakdown={fetchBreakdown}
          gameHref={(g) => `/press-box/${teamId}/game/${g.id}`}
          canSeeQuotes
          editTeamId={teamId}
          hiddenCols={hiddenCols}
          exportName="pressbox_stats"
        />
      )}
    </PressBoxShell>
  );
}
